import { KeyboardEvent, useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Grow from '@mui/material/Grow'
import FocusTrap from '@mui/material/Unstable_TrapFocus'
import Paper from '@mui/material/Paper'
import useMediaQuery from '@mui/material/useMediaQuery'
import useTheme from '@mui/material/styles/useTheme'
import { useLocation, useNavigate } from 'react-router-dom'
import { APP_BAR_HEIGHT } from '@/components/AppBar/constants'
import { RoutesData } from '@/router/RoutesData'
import AssistantChat from './AssistantChat'
import AssistantHeader from './AssistantHeader'
import { useAssistant } from './AssistantProvider'
import { trackAssistantEvent } from './analytics'
import {
  ASSISTANT_PANEL_ID,
  ASSISTANT_PANEL_WIDTH,
  aboveBottomInset,
  getAssistantPanelZIndex,
} from './layout'

const TITLE_ID = 'assistant-panel-title'

interface AssistantPanelProps {
  /** Called when the user closes the panel, to put focus back on its button. */
  onDismiss?: () => void
}

/**
 * Ask Attorney Share docked on the right, below the top bar, so it never
 * covers the nav. No backdrop: the page beside it stays usable. Full width
 * and modal on phones, where a tap on the top bar closes it. Shares its
 * conversation with the /assistant page.
 */
const AssistantPanel = ({ onDismiss }: AssistantPanelProps) => {
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'))
  const reduceMotion = useMediaQuery('@media (prefers-reduced-motion)')
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const assistant = useAssistant()
  const isOpen = Boolean(assistant?.isPanelOpen)
  const entryPoint = assistant?.panelEntryPoint ?? null
  const closePanel = assistant?.closePanel

  // Once per opening: the entry point stays put while the panel is open.
  useEffect(() => {
    if (isOpen) {
      trackAssistantEvent({
        event: 'assistant__opened',
        properties: {
          surface: 'panel',
          ...(entryPoint && { entry_point: entryPoint }),
        },
      })
    }
  }, [isOpen, entryPoint])

  // On phones the panel covers the page, so a link that opens another page
  // closes it. On wider screens the page is visible beside it; stay open.
  const lastPath = useRef(pathname)
  useEffect(() => {
    if (lastPath.current === pathname) return
    lastPath.current = pathname
    if (isPhone) closePanel?.()
  }, [pathname, isPhone, closePanel])

  if (!assistant) return null
  const { chat, startOver } = assistant

  const close = () => {
    assistant.closePanel()
    onDismiss?.()
  }

  const expand = () => {
    // The full page shows the same chat; the briefing comes along.
    assistant.closePanel()
    navigate(RoutesData.RAssistant.path)
  }

  const clear = () => {
    trackAssistantEvent({
      event: 'assistant__cleared',
      properties: { surface: 'panel' },
    })
    startOver()
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return
    event.stopPropagation()
    close()
  }

  return (
    <Grow
      in={assistant.isPanelOpen}
      style={{ transformOrigin: 'top right' }}
      timeout={reduceMotion ? 0 : 'auto'}
      mountOnEnter
      unmountOnExit
    >
      <Paper
        id={ASSISTANT_PANEL_ID}
        role="dialog"
        aria-modal={isPhone}
        aria-labelledby={TITLE_ID}
        data-testid="assistant-panel"
        elevation={8}
        onKeyDown={handleKeyDown}
        sx={{
          position: 'fixed',
          // Full width on phones, so it must also cover the case page's
          // fixed mobile action bar.
          zIndex: getAssistantPanelZIndex(theme),
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          // Below the top bar, to the bottom of the viewport; on tablets it
          // stops above the bottom nav or the case action bar.
          top: APP_BAR_HEIGHT,
          right: 0,
          bottom: { xs: 0, sm: aboveBottomInset(0) },
          left: { xs: 0, sm: 'auto' },
          width: { xs: '100%', sm: ASSISTANT_PANEL_WIDTH },
          maxWidth: '100%',
          borderRadius: 0,
        }}
      >
        {/* Full width on phones, so modal there: keep Tab inside. On wider
            screens the page beside it stays usable. The chat focuses its
            own input, and closing hands focus back to the top-bar button. */}
        {/* Off as soon as it closes: during the exit transition the panel is
            still mounted, and a live trap would pull focus back from the
            button. */}
        <FocusTrap
          open={isPhone && assistant.isPanelOpen}
          disableAutoFocus
          disableRestoreFocus
        >
          <Box
            tabIndex={-1}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              outline: 'none',
            }}
          >
            {/* Phones: the top bar stays in view above the modal panel, so a
                tap there closes it rather than opening a drawer or menu
                under it. Not the trap's direct child: the trap needs a child
                that takes a ref, and this listener doesn't. A tap on the
                top-bar button is left to the button: its touchend comes
                before its click, which would open the panel again. */}
            <ClickAwayListener
              onClickAway={event => {
                if (!isPhone) return
                const target = event.target as Node
                if (assistant.launcherRef.current?.contains(target)) return
                assistant.closePanel()
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <AssistantChat
                  key={chat.id}
                  chat={chat}
                  autoFocus
                  surface="panel"
                  suggestions={assistant.suggestions}
                  renderHeader={hasMessages => (
                    <AssistantHeader
                      onStartOver={clear}
                      canStartOver={hasMessages}
                      onExpand={expand}
                      onClose={close}
                      titleId={TITLE_ID}
                    />
                  )}
                />
              </Box>
            </ClickAwayListener>
          </Box>
        </FocusTrap>
      </Paper>
    </Grow>
  )
}

export default AssistantPanel
